import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import type { NotificationDto } from '@forest/contracts';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/cn';
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
  type NotificationFilter,
} from '@/features/notifications/useNotifications';
import {
  notificationIcon,
  relativeTime,
  renderNotificationText,
} from '@/features/notifications/render';

const FILTERS: NotificationFilter[] = ['all', 'unread'];

export function NotificationsPage() {
  const { t, i18n } = useTranslation(['notifications', 'common']);
  const navigate = useNavigate();
  const [filter, setFilter] = useState<NotificationFilter>('all');

  const listQ = useNotifications(filter, 20);
  const unreadQ = useUnreadCount();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const items: NotificationDto[] = listQ.data?.pages.flatMap((p) => p.items) ?? [];
  const unread = unreadQ.data ?? 0;

  const onItemClick = (n: NotificationDto) => {
    if (!n.read) markRead.mutate(n.id);
    if (n.url) navigate(n.url);
  };

  return (
    <div className="animate-fade-in space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 sm:text-2xl dark:text-slate-100">
            <Bell className="h-5 w-5 text-forest-600 dark:text-forest-300" />
            {t('notifications:page.title', { defaultValue: 'Notificări' })}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('notifications:page.subtitle', {
              defaultValue: 'Anunțuri, comentarii și actualizări care te privesc.',
            })}
          </p>
        </div>
        {unread > 0 ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
          >
            <CheckCheck className="mr-1 h-3.5 w-3.5" />
            {t('notifications:page.markAll', { defaultValue: 'Marchează toate ca citite' })}
          </Button>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            {FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                  filter === key
                    ? 'bg-forest-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700',
                )}
              >
                {t(`notifications:page.filter.${key}`, {
                  defaultValue: key === 'all' ? 'Toate' : 'Necitite',
                })}
                {key === 'unread' && unread > 0 ? (
                  <span className="ml-1 rounded-full bg-red-500 px-1.5 text-[10px] text-white">
                    {unread}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {listQ.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title={t('notifications:empty.title', { defaultValue: 'Nicio notificare' })}
              description={t('notifications:empty.description', {
                defaultValue: 'Vei vedea aici anunțuri și actualizări noi.',
              })}
            />
          ) : (
            <>
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((n) => {
                  const { title, body } = renderNotificationText(n, t);
                  const Icon = notificationIcon(n.type);
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => onItemClick(n)}
                        className={cn(
                          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/40',
                          !n.read && 'bg-forest-50/40 dark:bg-forest-900/10',
                        )}
                      >
                        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            {!n.read ? (
                              <span
                                className="h-2 w-2 flex-shrink-0 rounded-full bg-forest-500"
                                aria-hidden
                              />
                            ) : null}
                            <span
                              className={cn(
                                'text-sm',
                                n.read
                                  ? 'font-medium text-slate-700 dark:text-slate-300'
                                  : 'font-semibold text-slate-900 dark:text-slate-100',
                              )}
                            >
                              {title}
                            </span>
                          </span>
                          {body ? (
                            <span className="mt-0.5 block text-sm text-slate-500 dark:text-slate-400">
                              {body}
                            </span>
                          ) : null}
                          <span className="mt-1 block text-xs text-slate-400 dark:text-slate-500">
                            {relativeTime(n.createdAt, t, i18n.language)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {listQ.hasNextPage ? (
                <div className="flex justify-center border-t border-slate-100 p-3 dark:border-slate-800">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void listQ.fetchNextPage()}
                    disabled={listQ.isFetchingNextPage}
                    loading={listQ.isFetchingNextPage}
                  >
                    {t('notifications:page.loadMore', { defaultValue: 'Încarcă mai multe' })}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
